/**
 * CollapsedPanelBar
 *
 * A fixed-height (5px) color bar always present at the top of a panel layout.
 * Each collapsed panel is a colored chip-width segment. On hover the bar
 * expands as a smooth overlay to reveal text labels and action buttons —
 * content below never shifts.
 */

import { type LucideIcon } from 'lucide-react';

export interface CollapsedPanelAction {
  icon: LucideIcon;
  label: string;
  onClick: (e: React.MouseEvent) => void;
}

export interface CollapsedPanel {
  id: string;
  label: string;
  icon?: LucideIcon;
  onExpand: () => void;
  actions?: CollapsedPanelAction[];
}

interface CollapsedPanelBarProps {
  panels: CollapsedPanel[];
}

/** Base hue/sat from --primary (sky-500). Lightness steps up per item. */
const BASE_H = 199;
const BASE_S = 89;
const BASE_L = 38;
const L_STEP = 14;

function panelColor(index: number) {
  const l = Math.min(BASE_L + index * L_STEP, 80);
  return `hsl(${BASE_H} ${BASE_S}% ${l}%)`;
}

export function CollapsedPanelBar({ panels }: CollapsedPanelBarProps) {
  const hasItems = panels.length > 0;

  if (!hasItems) return null;

  return (
    <div className="relative shrink-0 h-[5px]">
      {hasItems && (
        /* Hover pad extends 24px below the 5px bar for a forgiving target */
        <div className="absolute left-0 top-0 z-20 pb-6 group">
          <div className="flex">
            {panels.map((panel, i) => {
              const Icon = panel.icon;
              return (
                <div
                  key={panel.id}
                  role="button"
                  tabIndex={0}
                  onClick={panel.onExpand}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      panel.onExpand();
                    }
                  }}
                  aria-label={`Expand ${panel.label}`}
                  className="focus-ring min-w-36 overflow-hidden text-left transition-all duration-300 ease-in-out h-[5px] group-hover:h-[32px] cursor-pointer"
                  style={{ backgroundColor: panelColor(i) }}
                >
                  {/* Content clipped at 5px, revealed on hover */}
                  <div className="flex items-center gap-1 px-3 py-1.5 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-in-out delay-100">
                    {Icon && <Icon className="h-3 w-3 text-white/90" />}
                    <span className="text-xs font-medium text-white/90">
                      {panel.label}
                    </span>
                    {panel.actions?.map((action) => {
                      const ActionIcon = action.icon;
                      return (
                        <button
                          key={action.label}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            action.onClick(e);
                          }}
                          className="focus-ring ml-1 rounded p-0.5 text-white/70 transition-colors hover:bg-white/20 hover:text-white"
                          title={action.label}
                        >
                          <ActionIcon className="h-3 w-3" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
